<template>
    <div class="side-nav-section" style="min-height: 1px; height: auto; display: flex; flex-direction: column;">
        <DxTreeList v-if="internalData.length"
                    id="treeList"
                    ref="tree-list"
                    :data-source="internalData"
                    :root-value="-1"
                    :show-row-lines="false"
                    :show-column-lines="false"
                    :show-borders="false"
                    :show-column-headers="false"
                    :expanded-row-keys="expandedRowKeys"
                    :auto-expand-all="false"
                    @cell-click="onCellClick"
                    @row-expanded="onRowCollapsedExpanded"
                    @row-collapsed="onRowCollapsedExpanded"
                    @row-prepared="onRowPrepared"
                    :on-content-ready="onContentReady"
                    key-expr="Id"
                    no-data-text
                    height="100%"
                    v-bind:class="{ 'is-editing': isEditMode }"
                    parent-id-expr="ParentId"
                    :search-panel="{visible: true, placeholder: 'Szukaj...', height:'30px'}"
                    class="folder-tree"
                    :focused-row-enabled="true"
                    @row-removing="onRowRemoving"
                    @nodes-initialized="onNodesInitialized"
                    @editing-start="onEditingStart">
            <DxLoadPanel :enabled="false" />
            <DxEditing :allow-updating.sync="allowEditing"
                       :allow-deleting.sync="allowDeleting"
                       mode="popup"
                       :texts="textOptions"
                       :use-icons="true">
            </DxEditing>
            <DxRowDragging :on-drag-change="onDragChange"
                           :on-reorder="onReorder"
                           :allow-drop-inside-item.sync="isEditMode"
                           :allow-reordering.sync="isEditMode"
                           :show-drag-icons="showDragIcons"
                           :on-drag-end="onDragEnd"
                           :on-drag-move="onDragMove" />

            <DxColumn data-field="Id" :allow-sorting="false" :visible="false" />
            <DxColumn data-field="Name"
                      :allow-sorting="true"
                      cell-template="fullNameTemplate"
                      :form-item="formItemOptions" />
            <DxColumn data-field="ItemType"
                      :visible="false"
                      :allow-sorting="false"
                      :form-item="formItemOptions" />
            <DxColumn data-field="IsSystem"
                      :visible="false"
                      :allow-sorting="false"
                      :form-item="formItemOptions" />
            <DxColumn data-field="ProcessSymbol"
                      :allow-sorting="false"
                      :visible="false"
                      :form-item="formItemOptions" />
            <DxColumn data-field="StepSymbol"
                      :allow-sorting="false"
                      :visible="false"
                      :form-item="formItemOptions" />
            <DxColumn data-field="GridSymbol"
                      :allow-sorting="false"
                      :visible="false"
                      :form-item="formItemOptions" />
            <DxColumn data-field="Link"
                      :allow-sorting="false"
                      :visible="false"
                      :form-item="formItemOptions" />
            <DxColumn data-field="ItemCount" :allow-sorting="false" :visible="false" />
            <DxColumn data-field="ParentId" :allow-sorting="false" :visible="false" />
            <DxColumn :width="40" type="buttons" :visible="isEditMode" />

            <template #fullNameTemplate="{ data }">
                <template v-if="data.data.ItemType === 0">
                    <div class="folder-tree-el">
                        <span class="folder-tree-icon"></span>
                        {{data.data.Name}}
                        <span class="sidebar-quantity"
                              v-if="getItemCount(data.data) > 0">{{getItemCount(data.data)}}</span>
                    </div>
                </template>
                <template v-else-if="data.data.ItemType === 1">
                    <div class="folder-tree-el"
                         @click="!isEditMode && openLinkInNewTab(data.data.Link)">
                        <span :class="data.data.Icon || 'fas fa-link'"></span>
                        {{data.data.Name}}
                    </div>
                </template>
                <template v-else-if="data.data.ItemType === 2">
                    <div class="folder-tree-el" @click="moveToWorkflowGrid(data.data.GridSymbol)">
                        <span>
                            <span :class="data.data.Icon || 'fas fa-link'"></span>
                            {{data.data.Name}}
                            <span class="sidebar-quantity"
                                  v-if="getItemCount(data.data) > 0">{{getItemCount(data.data)}}</span>
                        </span>
                    </div>
                </template>
                <template v-else-if="data.data.ItemType === 3">
                    <div class="folder-tree-el"
                         @click="moveToWorkflowStartStep(data.data.ProcessSymbol)">
                        <span>
                            <span :class="data.data.Icon || 'fas fa-link'"></span>
                            {{data.data.Name}}
                        </span>
                    </div>
                </template>
                <template v-else-if="data.data.ItemType === 4">
                    <div class="folder-tree-el"
                         @click="openRoute(data.data.Route)">
                        <span>
                            <span :class="data.data.Icon || 'fas fa-link'"></span>
                            {{data.data.Name}}
                        </span>
                    </div>
                </template>
            </template>
        </DxTreeList>

        <ul class="folder-tree-options">
            <li class="folder-tree-add"
                @click="addFolder"
                :title="$t('processMenu.newFolder')"
                v-if="isEditMode">
                <i class="far fa-folder"></i>
            </li>
            <li class="folder-tree-add"
                @click="addLink"
                :title="$t('processMenu.newLink')"
                v-if="isEditMode">
                <i class="far fa-link"></i>
            </li>
            <li>
                <span v-if="!isEditMode"
                      @click="editTreeSwitch">
                    <i class="fas fa-cog"></i>
                    Edytuj układ
                </span>
                <span v-if="!isEditMode"
                      @click="collapseAllRows">
                    <i class="far fa-chevron-up"></i>
                    Zwiń wszystko
                </span>
                <span v-if="isEditMode"
                      :title="$t('processMenu.resetTitle')"
                      @click="resetPopupVisible = !resetPopupVisible">
                    <i class="far fa-redo"></i>
                </span>
                <span class="separator"
                      v-if="isEditMode">
                </span>
                <span class="is-active"
                      v-if="isEditMode"
                      :title="$t('processMenu.saveChanges')"
                      @click="saveLayoutHandler">
                    <i class="far fa-check"></i>
                    Zapisz
                </span>
            </li>
        </ul>

        <rd-popup :visibility="editPopup.visible" :title="editPopup.title" :show-hide-button="false">
            <form @submit.prevent="saveForm">
                <dx-validation-group :ref="`validationGroup`">
                    <rd-popup-content>
                        <div class="row">
                            <rd-column :size="$columns.SixOnLg" v-if="editPopup.data">
                                <rd-form-group :label="$t('processMenu.name')">
                                    <DxTextBox v-model="editedElementData.Name">
                                        <dx-validator>
                                            <dx-required-rule :message="$t('fieldRules.reuiredShort')" />
                                        </dx-validator>
                                    </DxTextBox>
                                </rd-form-group>
                            </rd-column>
                            <rd-column :size="$columns.SixOnLg"
                                       v-if="editPopup.data && editPopup.data.ItemType == 1">
                                <rd-form-group label="Link">
                                    <DxTextBox v-model="editedElementData.Link">
                                        <dx-validator>
                                            <dx-required-rule :message="$t('fieldRules.reuiredShort')" />
                                        </dx-validator>
                                    </DxTextBox>
                                </rd-form-group>
                            </rd-column>
                        </div>
                    </rd-popup-content>
                    <rd-popup-actions>
                        <dx-button :use-submit-behavior="false"
                                   type="normal"
                                   :text="$t('buttons.cancel')"
                                   @click="closePopup"></dx-button>
                        <dx-button :use-submit-behavior="true"
                                   type="default"
                                   class="ml-1"
                                   @click="saveForm"
                                   :text="$t('buttons.save')"></dx-button>
                    </rd-popup-actions>
                </dx-validation-group>
            </form>
        </rd-popup>

        <rd-popup :visibility="deletePopup.visible" :title="deletePopup.title">
            <rd-popup-content>
                <div class="row">
                    <div class="col-xs-12">
                        <div>{{$t('processMenu.deleteMsg')}}</div>
                    </div>
                </div>
            </rd-popup-content>
            <rd-popup-actions>
                <dx-button :use-submit-behavior="false"
                           type="normal"
                           :text="$t('buttons.cancel')"
                           @click="closeDeletePopup">
                </dx-button>
                <dx-button :use-submit-behavior="false"
                           type="danger"
                           class="ml-1"
                           @click="deleteFrom"
                           :text="$t('buttons.delete')">
                </dx-button>
            </rd-popup-actions>
        </rd-popup>

        <rd-popup :visibility="resetPopupVisible" :title="$t('processMenu.resetTitle')">
            <rd-popup-content>
                <div class="row">
                    <div class="col-xs-12">
                        <div>{{$t('processMenu.resetMsg')}}</div>
                    </div>
                </div>
            </rd-popup-content>
            <rd-popup-actions>
                <dx-button :use-submit-behavior="false"
                           type="normal"
                           :text="$t('buttons.cancel')"
                           @click="resetPopupVisible = !resetPopupVisible">
                </dx-button>
                <dx-button :use-submit-behavior="false"
                           type="danger"
                           class="ml-1"
                           @click="resetLayoutHandler"
                           :text="$t('buttons.reset')">
                </dx-button>
            </rd-popup-actions>
        </rd-popup>

    </div>
</template>
<script>
    // @ is an alias to /src

    import {
        DxTreeList,
        DxColumn,
        DxRowDragging,
        DxLoadPanel,
        DxEditing,
    } from "devextreme-vue/tree-list";
    import DxButton from 'devextreme-vue/button';
    import { DxRequiredRule, DxValidator } from 'devextreme-vue/validator';
    import DxTextBox from 'devextreme-vue/text-box';
    import DxValidationGroup from "devextreme-vue/validation-group";
    import DataSource from "devextreme/data/data_source";

    import { mapActions } from 'vuex';
    import router from "../../../../../router";
    import commonMessages from "../../../locales";
    import localMessages from "../../locales";

    import { v4 as uuidv4 } from 'uuid';

    const INDEX_STORE = "processesStores/processesHomeStores/indexStore";

    export default {
        name: "container",
        props: ["componentConfig"],
        data() {
            return {
                isSelected: false,
                internalData: [],
                isEditMode: false,
                showDragIcons: false,
                lastSelectedTreeListRowKey: null,
                token: null,
                version: null,
                treeDataTimer: null,
                textOptions: {
                    confirmDeleteMessage: ""
                },
                popupOptions: {
                    title: this.$t('processMenu.fieldEdit'),
                    showTitle: true,
                    contentTemplate: "popupTemplate",
                },
                formItemOptions: {
                    visible: true,
                },
                resetPopupVisible: false,
                deletePopup: {
                    visible: false,
                    title: this.$t('processMenu.recordDel'),
                    data: null,
                    key: null,
                    cancel: true,
                },
                editPopup: {
                    visible: false,
                    title: null,
                    data: null,
                    oldName: null,
                    oldLink: null,
                },
                editedElementData: {
                    Name: null,
                    Link: null,
                    Id: null,
                },
            };
        },
        computed: {
            getSourceItems: function () {
                return this.internalData;
            },
            expandedRowKeys: function () {
                let expandedRowKeys;
                let localStorageExpandedRowKeys = localStorage.getItem("treeListExpandedRowKeys");
                if (localStorageExpandedRowKeys) {
                    expandedRowKeys = localStorageExpandedRowKeys.split(",")
                } else {
                    expandedRowKeys = [1];
                }
                return expandedRowKeys;
            }
        },
        mounted() {
            setTimeout(() => {
                this.setTreeDataHandler().then((data) => {
                    this.setTreeData(data);
                });
            }, 300);
        },
        methods: {
            ...mapActions(INDEX_STORE, ['setTreeDataHandler', 'resetLayout', 'saveLayout']),
            allowDeleting: function (e) {
                return !e.row.data.IsSystem;
            },
            allowEditing: function (e) {
                return !e.row.data.IsSystem;
            },
            closePopup: function () {
                this.editedElementData.Name = this.editPopup.oldName;
                this.editedElementData.Link = this.editPopup.oldLink;
                this.editPopup.title = null;
                this.editPopup.visible = false;
                this.editPopup.data = null;
                this.editPopup.oldName = null;
                this.editPopup.oldLink = null;
            },
            closeDeletePopup: function () {
                this.deletePopup.title = null;
                this.deletePopup.visible = false;
                this.deletePopup.data = null;
                this.deletePopup.cancel = true;
                this.deletePopup.key = null;
            },
            onContentReady: function (e) {
                let lastSelectedTreeListRowKey = this.lastSelectedTreeListRowKey;
                let treeListInstance = this.$refs["tree-list"].instance;
                if (lastSelectedTreeListRowKey && !this.isSelected) {
                    treeListInstance.option("focusedRowKey", lastSelectedTreeListRowKey);
                    treeListInstance.option("navigateToRow", lastSelectedTreeListRowKey);
                    this.isSelected = true;
                }
            },
            onRowPrepared: function (e) {
                if (e.data.ItemType == 4) {
                    //dashboards
                    if (this.$route.name == e.data.Route) {
                        this.lastSelectedTreeListRowKey = e.key;
                        return;
                    }
                }
                let param = this.$route.params.processSymbol || this.$route.params.gridSymbol;
                let symbol = e.data.ProcessSymbol || e.data.GridSymbol;
                if (symbol && param) {
                    if (param == symbol) {
                        this.lastSelectedTreeListRowKey = e.key;
                        return;
                    }
                }
            },
            saveForm: function (e) {
                let validateResult = this.$refs["validationGroup"].instance.validate();
                if (validateResult.isValid) {
                    this.editPopup.title = null;
                    this.editPopup.visible = false;
                    this.editPopup.data = null;
                    this.editPopup.oldName = null;
                    this.editPopup.oldLink = null;
                }
            },
            deleteFrom: function (e) {
                var id = this.deletePopup.data.Id;
                var parentId = this.deletePopup.data.ParentId;
                if (this.deletePopup.data.Id.ItemType === 0) {
                    this.getSourceItems.forEach((el, i) => {
                        if (el.ParentId === id) {
                            el.ParentId = parentId;
                        }
                    });
                }
                this.deletePopup.cancel = false;
                let rowIndexToDelete = this.$refs["tree-list"].instance.getRowIndexByKey(this.deletePopup.key)
                this.$refs["tree-list"].instance.deleteRow(rowIndexToDelete);
                this.deletePopup.title = null;
                this.deletePopup.visible = false;
                this.deletePopup.data = null;
                this.deletePopup.cancel = true;
            },
            getDataSource: function () {
                return new DataSource({
                    store: this.getSourceItems,
                });
            },
            onNodesInitialized: function (e) {
                e.component.forEachNode(e.root.children, function (node) {
                    if (node.visible && !node.hasChildren && node.children.length) {
                        node.hasChildren = true;
                        node.children.forEach(function (node) {
                            node.visible = true;
                        });
                    }
                });
            },
            onRowCollapsedExpanded: function (e) {
                let expandedRowKeys = this.$refs["tree-list"].instance.option("expandedRowKeys")
                localStorage.removeItem('treeListExpandedRowKeys');
                localStorage.setItem('treeListExpandedRowKeys', expandedRowKeys);
            },
            collapseAllRows: function (e) {
                let instance = this.$refs["tree-list"].instance;
                let expandedRowKeys = instance.option("expandedRowKeys");
                expandedRowKeys.forEach(key => {
                    this.$refs["tree-list"].instance.collapseRow(key);
                })
            },
            onCellClick: function (e) {
                if (e.event.target.classList.contains('folder-tree-el')) {

                    e.row.isSelected = false;
                    var rowKey = e.row.key;
                    var rowIndex = e.row.rowIndex;
                    var isExpanded = e.row.cells[0].row.isExpanded;

                    if (isExpanded) {
                        e.component.collapseRow(rowKey);
                    } else {
                        e.component.expandRow(rowKey);
                    }
                    e.component.navigateToRow(rowKey);
                }
                e.row.isSelected = false;
            },
            onEditingStart: function (e) {
                e.cancel = true;
                this.$refs["validationGroup"].instance.validate();
                this.editPopup.title = e.data.Name;
                this.editPopup.oldLink = e.data.Link || '';
                this.editPopup.oldName = e.data.Name || '';
                this.editPopup.visible = true;
                this.editPopup.data = e.data;
                this.editedElementData = e.data;
                this.editedElementData.Id = e.data.Id;
            },
            resetLayoutHandler: function () {
                var localThis = this;
                if (!localThis.isEditMode) {
                    return;
                }
                this.resetLayout(this.token).then((data) => {
                    this.resetPopupVisible = !this.resetPopupVisible;
                    this.internalData = [];
                    this.isEditMode = false;
                    this.setTreeData(data);
                });
            },
            saveLayoutHandler: function () {
                let payload = {
                    Version: this.version,
                    Items: this.getSourceItems,
                    Token: this.token
                }
                this.saveLayout(payload).then((response) => {
                    this.isEditMode = !this.isEditMode;
                    let notificationMessage = "",
                        notificationType = "";

                    if (response) {
                        notificationMessage = this.$t('index.processMenu.saved');
                        notificationType = 'success';
                    } else {
                        notificationMessage = this.$t('index.processMenu.saveFailed');
                        notificationType = 'success';
                    }

                    this.$notify({
                        width: 'auto',
                        position: { my: 'center bottom', at: 'center bottom', of: window, collision: 'fit', offset: '0 -5' },
                        displayTime: 1800,
                        message: notificationMessage,
                        type: notificationType,
                    });
                });
            },
            setTreeData: function (treeListData) {
                var localThis = this;
                if (localThis.isEditMode) {
                    return;
                }

                var dataToRemove = [];
                localThis.internalData.forEach((item, index) => {
                    var foundData = treeListData.Model.Items.filter(function (modelItem, index, arr) { return modelItem.Id === item.Id; });
                    if (!foundData.length) {
                        dataToRemove.push(index);
                    }
                }
                );
                dataToRemove.reverse();
                dataToRemove.forEach((item) => {
                    localThis.internalData.splice(item, 1);
                });
                treeListData.Model.Items.forEach((modelItem) => {

                    var foundData = localThis.internalData.filter(function (item, index, arr) { return modelItem.Id === item.Id; });
                    if (!foundData.length) {
                        localThis.internalData.push(modelItem);
                    } else {
                        if (foundData[0].ParentId != modelItem.ParentId) {
                            foundData[0].ParentId = modelItem.ParentId;
                        }
                        if (foundData[0].ItemType == 0 || foundData[0].ItemType == 2) {
                            foundData[0].ItemCount = modelItem.ItemCount;
                        }
                        if (foundData[0].ItemType == 1) {
                            foundData[0].Link = modelItem.Link;
                        } else if (foundData[0].ItemType == 2) {
                            foundData[0].GridSymbol = modelItem.GridSymbol;
                        } else if (foundData[0].ItemType == 3) {
                            foundData[0].StepSymbol = modelItem.StepSymbol;
                            foundData[0].ProcessSymbol = modelItem.ProcessSymbol;
                        }
                    }
                });
                localThis.token = treeListData.Token;
                localThis.version = treeListData.Model.Version;
                localThis.treeDataTimer = setTimeout(function () {
                    localThis.setTreeDataHandler().then((data) => {
                        localThis.setTreeData(data);
                    });
                }, 10 * 1000);

            },
            moveToWorkflowGrid: function (gridSymbol) {
                if (this.isEditMode) {
                    return;
                }
                router.push({
                    name: "workflow.index.taskgrid",
                    params: { gridSymbol: gridSymbol },
                });
                this.onLinkClick();
            },
            moveToWorkflowStartStep: function (processSymbol) {
                if (this.isEditMode) {
                    return;
                }
                router.push({
                    name: "workflow.process.index",
                    params: { processSymbol: processSymbol },
                });
                this.onLinkClick();
            },
            openRoute: function (route) {
                if (this.isEditMode) {
                    return;
                }
                router.push({
                    name: route
                });
                this.onLinkClick();
            },
            openLinkInNewTab(url) {
                if (this.isEditMode) {
                    return;
                } else {
                    const link = document.createElement('a');
                    link.href = url;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                }

            },
            onRowRemoving: function (e) {
                let cancel = this.deletePopup.cancel;
                e.cancel = cancel;
                if (cancel) {
                    this.deletePopup.title = e.data.Name;
                    this.deletePopup.visible = true;
                    this.deletePopup.data = e.data;
                    this.deletePopup.key = e.key
                }

                //var id = e.data.Id;
                //var parentId = e.data.ParentId;
                //if (e.data.ItemType === 0) {
                //    this.getSourceItems.forEach((el, i) => {
                //        if (el.ParentId === id) {
                //            el.ParentId = parentId;
                //        }
                //    });
                //}
            },
            onDragEnd: function (e) {
                var rowKey = e.toComponent.getKeyByRowIndex(e.toIndex);
                var rowData = e.toComponent.getNodeByKey(rowKey).data;
                if (e.dropInsideItem && rowData.ItemType !== 0) {
                    e.cancel = true;
                }
            },
            // scroll to the bottom of scrollview after link/folder is added
            rescrollContent: function () {
                const scrollable = this.$refs["tree-list"].instance.getScrollable();
                // total height of scrollview
                const scrollAreaHeight = scrollable.scrollHeight();
                // height of single <tr> in menu
                const singleRowHeight = document.querySelector(".dx-row.dx-data-row").offsetHeight;
                // scroll to the bottom
                scrollable.scrollTo(scrollAreaHeight + singleRowHeight);
            },
            async addFolder() {
                if (!this.isEditMode) {
                    return;
                }
                var folder = {
                    Id: uuidv4(),
                    ItemType: 0,
                    Name: this.$t('processMenu.newFolder'),
                    ItemCount: 0,
                    IsSystem: false,
                    ParentId: null,
                };
                await this.internalData.push(folder);
                await this.$refs["tree-list"].instance.saveEditData();
                this.rescrollContent();
            },
            async addLink() {
                if (!this.isEditMode) {
                    return;
                }
                var link = {
                    Id: uuidv4(),
                    ItemType: 1,
                    Link: this.$('processMenu.enterLink'),
                    ParentId: null,
                    IsSystem: false,
                    Name: this.$('processMenu.newLink'),
                };
                await this.internalData.push(link);
                await this.$refs["tree-list"].instance.saveEditData();
                this.rescrollContent();
            },
            editTreeSwitch: function () {
                this.isEditMode = !this.isEditMode;
            },
            onDragMove: function (e) {
                var rowKey = e.toComponent.getKeyByRowIndex(e.toIndex);
                var rowData = e.toComponent.getNodeByKey(rowKey).data;

                if (rowData.ItemType !== 0) {
                    e.toComponent.$element()[0].style.border = "none";
                }
            },
            onDragChange(e) {
                let visibleRows = e.component.getVisibleRows(),
                    sourceNode = e.component.getNodeByKey(e.itemData.Id),
                    targetNode = visibleRows[e.toIndex].node;

                while (targetNode && targetNode.data) {
                    if (targetNode.data.Id === sourceNode.data.Id) {
                        e.cancel = true;
                        break;
                    }
                    targetNode = targetNode.parent;
                }
            },
            onReorder(e) {
                let visibleRows = e.component.getVisibleRows(),
                    sourceData = e.itemData,
                    targetData = visibleRows[e.toIndex].data;

                if (e.dropInsideItem) {
                    e.itemData.ParentId = targetData.Id;
                    e.component.refresh();
                } else {
                    let sourceIndex = this.getSourceItems.indexOf(sourceData),
                        targetIndex = this.getSourceItems.indexOf(targetData);

                    if (sourceData.ParentId !== targetData.ParentId) {
                        sourceData.ParentId = targetData.ParentId;
                        if (e.toIndex > e.fromIndex) {
                            targetIndex++;
                        }
                    }

                    this.getSourceItems.splice(sourceIndex, 1);
                    this.getSourceItems.splice(targetIndex, 0, sourceData);
                }
                this.recountItemCounts();
            },
            getItemById: function (id) {
                var item = null;
                this.getSourceItems.forEach((el, i) => {
                    if (el.Id === id) {
                        item = el;
                        return;
                    }
                });
                return item;
            },
            getItemCount: function (data) {
                return data.ItemCount;
            },
            recountItemCounts: function () {
                this.getSourceItems.forEach((el, i) => {
                    if (el.ItemType === 0) {
                        el.ItemCount = 0;
                        return;
                    }
                });
                this.getSourceItems.forEach((el, i) => {
                    if (el.ItemType === 2) {
                        var parentId = el.ParentId;
                        while (parentId != null) {
                            var parent = this.getItemById(parentId);
                            parent.ItemCount += el.ItemCount;
                            parentId = parent.ParentId;
                        }
                        return;
                    }
                });
            },
            onLinkClick: function () {
                this.$emit('linkClicked', true);
            }
        },
        destroyed() {
            if (this.treeDataTimer) {
                clearTimeout(this.treeDataTimer);
                this.treeDataTimer = null;
            }
        },
        components: {
            DxTreeList,
            DxColumn,
            DxRowDragging,
            DxLoadPanel,
            DxEditing,
            DxValidationGroup,
            DxRequiredRule,
            DxValidator,
            DxTextBox,
            DxButton
        },
        i18n: {
            messages: localMessages.messages,
            sharedMessages: commonMessages.messages,
        },
    };
</script>